// --- React
import { useMemo } from "react";
// --- Redux
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { createSelector } from "@reduxjs/toolkit";
// --- Actions
import * as allActions from "./actions";
// --- Domain
import { STAT_TILE_FILTER } from "ppt/constants";
import { getPromoTileBucket } from "ppt/utilities";

export function usePPTActions() {
  const dispatch = useDispatch();

  // Bind all action creators and thunks to the dispatch function
  return useMemo(
    () => bindActionCreators({ ...allActions }, dispatch),
    [dispatch],
  );
}

const deserializeDate = (date) => {
  if (typeof date === "number") {
    return new Date(date);
  }
  return date ?? null;
};

// Memoized selectors for each piece of state that needs deserialization.
// Standard pattern: one selector per state field, each handles its own transformation.
// This ensures stable references and prevents unnecessary re-renders.

const selectDeserializedPromos = createSelector(
  [(state) => state.ppt?.promos],
  (promos) =>
    (promos || []).map((promo) => ({
      ...promo,
      startDate: deserializeDate(promo.startDate),
      endDate: deserializeDate(promo.endDate),
    })),
);

/**
 * Promo counts per stat tile, keyed by STAT_TILE_FILTER value. Derived from the
 * raw promos slice (no date deserialization needed) and memoized, so the tiles
 * recompute only when the list actually changes.
 */
const selectPromosTileCounts = createSelector(
  [(state) => state.ppt?.promos],
  (promos) => {
    const counts = {
      [STAT_TILE_FILTER.OK]: 0,
      [STAT_TILE_FILTER.WARNING]: 0,
      [STAT_TILE_FILTER.ERROR]: 0,
    };
    (promos || []).forEach((promo) => {
      const bucket = getPromoTileBucket(promo);
      if (bucket in counts) counts[bucket] += 1;
    });
    return counts;
  },
);

const selectDeserializedPromotionDetail = createSelector(
  [(state) => state.ppt?.promotionDetail],
  (promotionDetail) =>
    promotionDetail
      ? {
          ...promotionDetail,
          startDate: deserializeDate(promotionDetail.startDate),
          endDate: deserializeDate(promotionDetail.endDate),
        }
      : promotionDetail,
);

const selectDeserializedDiscountDetails = createSelector(
  [(state) => state.ppt?.discountDetails],
  (discountDetails) =>
    (discountDetails || []).map((discount) => ({
      ...discount,
      startDate: deserializeDate(discount.startDate),
      endDate: deserializeDate(discount.endDate),
    })),
);

const selectDeserializedPromotionEvents = createSelector(
  [(state) => state.ppt?.promotionEvents],
  (promotionEvents) =>
    (promotionEvents || []).map((event) => ({
      ...event,
      occurredAt: deserializeDate(event.occurredAt),
    })),
);

const selectDeserializedPromotionIssues = createSelector(
  [(state) => state.ppt?.promotionIssues],
  (promotionIssues) =>
    (promotionIssues || []).map((issue) => ({
      ...issue,
      createdAt: deserializeDate(issue.createdAt),
    })),
);

const selectDeserializedPromotionIssuesData = createSelector(
  [(state) => state.ppt?.promotionIssuesData],
  (data) => {
    if (!data) return null;

    const deserializeIssues = (issues) =>
      (issues || []).map((issue) => ({
        ...issue,
        createdAt: deserializeDate(issue.createdAt),
      }));

    return {
      ...data,
      promotionLevel: (data.promotionLevel || []).map((typeGroup) => ({
        ...typeGroup,
        issues: deserializeIssues(typeGroup.issues),
      })),
    };
  },
);

const selectDeserializedSelectedDiscount = createSelector(
  [(state) => state.ppt?.selectedDiscount],
  (selectedDiscount) =>
    selectedDiscount
      ? {
          ...selectedDiscount,
          startDate: deserializeDate(selectedDiscount.startDate),
          endDate: deserializeDate(selectedDiscount.endDate),
        }
      : selectedDiscount,
);

const selectDeserializedDiscountIssues = createSelector(
  [(state) => state.ppt?.discountIssues],
  (data) => {
    if (!data) return null;

    const deserializeIssues = (issues) =>
      (issues || []).map((issue) => ({
        ...issue,
        createdAt: deserializeDate(issue.createdAt),
      }));

    return {
      ...data,
      byType: (data.byType || []).map((type) => ({
        ...type,
        issues: deserializeIssues(type.issues),
      })),
    };
  },
);

// TODO: Consider moving to granular selectors instead of a single monolithic selector
// Main selector combines all memoized selectors and passes through other state.
const selectPPTStateWithDeserialization = createSelector(
  [
    (state) => state.ppt || {},
    selectDeserializedPromos,
    selectPromosTileCounts,
    selectDeserializedPromotionDetail,
    selectDeserializedDiscountDetails,
    selectDeserializedPromotionEvents,
    selectDeserializedPromotionIssues,
    selectDeserializedPromotionIssuesData,
    selectDeserializedSelectedDiscount,
    selectDeserializedDiscountIssues,
  ],
  (
    pptState,
    promos,
    promosTileCounts,
    promotionDetail,
    discountDetails,
    promotionEvents,
    promotionIssues,
    promotionIssuesData,
    selectedDiscount,
    discountIssues,
  ) => {
    return {
      ...pptState,
      promos,
      promosTileCounts,
      promotionDetail,
      discountDetails,
      promotionEvents,
      promotionIssues,
      promotionIssuesData,
      selectedDiscount,
      discountIssues,
    };
  },
);

export function usePPTState() {
  return useSelector(selectPPTStateWithDeserialization);
}
